import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DataGridPro,
  GridColDef,
  GridRowParams,
} from "@mui/x-data-grid-pro";
import { jaJPText } from "../stores/Mui_JapanesTxist";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  GlobalStyles,
} from "@mui/material";

import Grid from "@mui/material/Grid";
import { alpha, ThemeProvider, useTheme } from "@mui/material/styles";

import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

import axios from "axios";
import ConstList from "../utils/ConstList";

import SearchIcon from "@mui/icons-material/Search";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

import theme from "../theme";

/* =========================================================
 型定義
========================================================= */

type StoppedCustomerRow = {
  id: number;
  company_id: number;

  company_name: string;
  business_description: string;
  company_telephone: string;
  company_mail: string;
  company_address: string;
  company_website: string;

  last_trade_datetime: string | null;
  last_sales_date: string | null;
  last_sales_person: string;
  latest_sales_status: string;
  latest_proposed_service: string;
  sales_history_count: number;

  deleted_by: string;
  deleted_day: string | null;
  delete_reason: string;
  updated_by: string;
  updated_day: string | null;
};

type SalesHistoryRow = {
  sales_history_id: number;
  sales_date: string | null;
  contact_person: string;
  sales_person: string;
  sales_status: string;
  purchase_intention: string;
  proposed_service: string;
  sales_approach_method: string;
  proposal_details: string;
  next_action: string;
  memo: string;
  man_hours: number | null;
  created_by: string;
  created_day: string | null;
  edited_by: string;
  edited_day: string | null;
};

/* =========================================================
 共通関数
========================================================= */

const toText = (value: any) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const toNumber = (value: any, fallback = 0) => {
  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
};

const normalizeText = (text: string | null | undefined) => {
  if (typeof text !== "string") return "";
  return text.normalize("NFKC").toLowerCase();
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return "-";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  return `${year}/${month}/${day}`;
};

const formatDateTime = (date: string | null | undefined) => {
  if (!date) return "-";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const hour = String(parsed.getHours()).padStart(2, "0");
  const minute = String(parsed.getMinutes()).padStart(2, "0");

  return `${year}/${month}/${day} ${hour}:${minute}`;
};

const getLastTradeDate = (row: StoppedCustomerRow) => {
  return row.last_trade_datetime || row.last_sales_date || null;
};

const getStoppedDays = (date: string | null | undefined) => {
  if (!date) return null;

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);

  const diff = today.getTime() - parsed.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

/* =========================================================
 メイン
========================================================= */

const StoppedCustomersData = () => {
  const muiTheme = useTheme();
  const loginUserData = useRecoilValue(LoginUserAtom);

  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [detailErrorMessage, setDetailErrorMessage] = useState<string | null>(
    null
  );

  const [rows, setRows] = useState<StoppedCustomerRow[]>([]);
  const [searchText, setSearchText] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<StoppedCustomerRow | null>(
    null
  );
  const [salesHistories, setSalesHistories] = useState<SalesHistoryRow[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<SalesHistoryRow | null>(
    null
  );

  const [reactivateTarget, setReactivateTarget] =
    useState<StoppedCustomerRow | null>(null);

  const getLoginUserName = () => {
    return (
      loginUserData.userName ||
      loginUserData.email ||
      loginUserData.userId
    );
  };

  /* =========================================================
   一覧取得
  ========================================================= */

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    const postData = {
      flag: "GetStoppedProsCustomers",
      access_token: loginUserData?.accessToken,
      algorithm: loginUserData?.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "StoppedCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      console.log("取引停止企業一覧 取得結果", response.data);

      if (response.data?.data === false) {
        setErrorMessage(response.data?.message ?? "データ取得に失敗しました。");

        if (response.data?.reDirect) {
          await ConstList.SLEEP(5000);
          window.location.href = response.data.reDirect;
        }

        return;
      }

      if (response.data?.status !== 1) {
        setErrorMessage(
          response.data?.message ?? "取引停止企業の取得に失敗しました。"
        );
        setRows([]);
        return;
      }

      const rawList = response.data?.data ?? [];

      const mappedRows: StoppedCustomerRow[] = rawList.map(
        (value: any, index: number) => {
          const companyId = toNumber(value.company_id ?? value.customer_id);

          return {
            id: companyId || index + 1,
            company_id: companyId,

            company_name: toText(value.company_name ?? value.customer_name),
            business_description: toText(value.business_description),
            company_telephone: toText(value.company_telephone),
            company_mail: toText(value.company_mail),
            company_address: toText(value.company_address),
            company_website: toText(value.company_website),

            last_trade_datetime:
              value.last_trade_datetime ??
              value.last_trade_date ??
              value.last_contact_date ??
              value.last_sales_date ??
              null,

            last_sales_date: value.last_sales_date ?? null,
            last_sales_person: toText(
              value.last_sales_person ?? value.sales_person
            ),
            latest_sales_status: toText(
              value.latest_sales_status ?? value.sales_status
            ),
            latest_proposed_service: toText(
              value.latest_proposed_service ?? value.proposed_service
            ),
            sales_history_count: toNumber(
              value.sales_history_count ?? value.history_count,
              0
            ),

            deleted_by: toText(value.deleted_by),
            deleted_day: value.deleted_day ?? null,
            delete_reason: toText(value.delete_reason),
            updated_by: toText(value.updated_by),
            updated_day: value.updated_day ?? null,
          };
        }
      );

      setRows(mappedRows);
    } catch (error) {
      console.error("取引停止企業データ取得エラー:", error);
      setErrorMessage("取引停止企業データの取得に失敗しました。");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    loginUserData?.accessToken,
    loginUserData?.algorithm,
  ]);

  useEffect(() => {
    if (!loginUserData?.accessToken || !loginUserData?.algorithm) return;
    fetchData();
  }, [fetchData, loginUserData?.accessToken, loginUserData?.algorithm]);

  /* =========================================================
   詳細・履歴取得
  ========================================================= */

  const fetchDetail = async (row: StoppedCustomerRow) => {
    setSelectedRow(row);
    setDetailOpen(true);
    setDetailErrorMessage(null);
    setSalesHistories([]);
    setSelectedHistory(null);
    setHistoryLoading(true);

    const postData = {
      flag: "GetStoppedProsCustomerDetail",
      company_id: row.company_id,
      deleted_day: row.deleted_day,
      access_token: loginUserData?.accessToken,
      algorithm: loginUserData?.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "StoppedCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      console.log("取引停止企業詳細 取得結果", response.data);

      if (response.data?.data === false || response.data?.status !== 1) {
        setDetailErrorMessage(
          response.data?.message ?? "取引履歴の取得に失敗しました。"
        );
        return;
      }

      const payload = response.data?.data ?? {};

      const rawHistories =
        payload?.sales_history ??
        payload?.histories ??
        payload?.sales_histories ??
        [];

      const mappedHistories: SalesHistoryRow[] = Array.isArray(rawHistories)
        ? rawHistories.map((value: any, index: number) => ({
            sales_history_id: toNumber(
              value.sales_history_id ?? value.id ?? value.sales_record_id,
              index + 1
            ),
            sales_date: value.sales_date ?? null,
            contact_person: toText(value.contact_person),
            sales_person: toText(value.sales_person),
            sales_status: toText(value.sales_status ?? value.status),
            purchase_intention: toText(value.purchase_intention),
            proposed_service: toText(value.proposed_service),
            sales_approach_method: toText(value.sales_approach_method),
            proposal_details: toText(value.proposal_details),
            next_action: toText(value.next_action),
            memo: toText(value.memo ?? value.remarks ?? value.sales_memo),
            man_hours:
              value.man_hours === null ||
              value.man_hours === undefined ||
              value.man_hours === ""
                ? null
                : toNumber(value.man_hours, 0),
            created_by: toText(value.created_by),
            created_day: value.created_day ?? null,
            edited_by: toText(value.edited_by),
            edited_day: value.edited_day ?? null,
          }))
        : [];

      setSalesHistories(mappedHistories);
    } catch (error) {
      console.error("取引停止企業詳細取得エラー:", error);
      setDetailErrorMessage("取引履歴の取得に失敗しました。");
    } finally {
      setHistoryLoading(false);
    }
  };

  /* =========================================================
   取引再開
  ========================================================= */

  const handleReactivate = async () => {
    if (!reactivateTarget) return;

    setReactivating(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    const postData = {
      flag: "ReactivateStoppedProsCustomer",
      company_id: reactivateTarget.company_id,
      updated_by: getLoginUserName(),
      access_token: loginUserData?.accessToken,
      algorithm: loginUserData?.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "StoppedCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      console.log("取引再開 結果", response.data);

      if (response.data?.data === false || response.data?.status !== 1) {
        throw new Error(response.data?.message ?? "取引再開に失敗しました。");
      }

      setRows((prev) =>
        prev.filter((row) => row.company_id !== reactivateTarget.company_id)
      );

      setSuccessMessage(`${reactivateTarget.company_name} を取引再開しました。`);

      if (selectedRow?.company_id === reactivateTarget.company_id) {
        setDetailOpen(false);
        setSelectedRow(null);
        setSalesHistories([]);
        setSelectedHistory(null);
      }

      setReactivateTarget(null);
    } catch (error: any) {
      console.error("取引再開エラー:", error);
      setErrorMessage(error?.message ?? "取引再開に失敗しました。");
    } finally {
      setReactivating(false);
    }
  };

  /* =========================================================
   検索
  ========================================================= */

  const filteredRows = useMemo(() => {
    const keyword = normalizeText(searchText);

    if (!keyword) return rows;

    return rows.filter((row) => {
      const targetText = [
        row.company_name,
        row.business_description,
        row.company_telephone,
        row.company_mail,
        row.company_address,
        row.company_website,
        row.deleted_by,
        row.delete_reason,
        row.last_sales_person,
        row.latest_sales_status,
        row.latest_proposed_service,
      ].join(" ");

      return normalizeText(targetText).includes(keyword);
    });
  }, [rows, searchText]);

  /* =========================================================
   DataGrid
  ========================================================= */

  const columns: GridColDef<StoppedCustomerRow>[] = [
    {
      field: "company_name",
      headerName: "会社名",
      minWidth: 300,
      flex: 1.4,
      renderCell: (params) => (
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography
            fontWeight={800}
            sx={{
              color: muiTheme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={params.row.company_name}
          >
            {params.row.company_name || "-"}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: "rgba(232,238,245,0.65)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
            title={params.row.business_description}
          >
            ID：{params.row.company_id} / {params.row.business_description || "-"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "last_trade_datetime",
      headerName: "最終取引日時",
      minWidth: 180,
      flex: 0.85,
      renderCell: (params) => (
        <Stack spacing={0.25}>
          <Typography fontWeight={700} sx={{ color: muiTheme.palette.text.primary }}>
            {formatDateTime(getLastTradeDate(params.row))}
          </Typography>

          <Typography variant="caption" sx={{ color: "rgba(232,238,245,0.65)" }}>
            担当：{params.row.last_sales_person || "-"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "latest_sales_status",
      headerName: "停止前状況",
      minWidth: 160,
      flex: 0.75,
      renderCell: (params) => (
        <Stack spacing={0.4}>
          <Chip
            size="small"
            label={params.row.latest_sales_status || "未設定"}
            sx={{
              width: "fit-content",
              color: muiTheme.palette.primary.main,
              backgroundColor: alpha("#ff8579", 0.12),
              border: `1px solid ${alpha("#ff8579", 0.35)}`,
              fontWeight: 700,
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: "rgba(232,238,245,0.65)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 150,
            }}
            title={params.row.latest_proposed_service}
          >
            {params.row.latest_proposed_service || "-"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "sales_history_count",
      headerName: "履歴",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack alignItems="center" spacing={0.2}>
          <HistoryOutlinedIcon
            fontSize="small"
            sx={{ color: "rgba(232,238,245,0.65)" }}
          />
          <Typography variant="caption" sx={{ color: "rgba(232,238,245,0.65)" }}>
            {params.row.sales_history_count}件
          </Typography>
        </Stack>
      ),
    },
    {
      field: "deleted_day",
      headerName: "取引停止日",
      width: 150,
      renderCell: (params) => {
        const stoppedDays = getStoppedDays(params.row.deleted_day);

        return (
          <Stack spacing={0.25}>
            <Typography fontWeight={700} sx={{ color: muiTheme.palette.text.primary }}>
              {formatDate(params.row.deleted_day)}
            </Typography>

            <Typography variant="caption" sx={{ color: "rgba(232,238,245,0.65)" }}>
              {stoppedDays === null ? "-" : `${stoppedDays}日経過`}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "deleted_by",
      headerName: "使用停止者",
      width: 130,
      renderCell: (params) => (
        <Typography sx={{ color: muiTheme.palette.text.primary }}>
          {params.row.deleted_by || "-"}
        </Typography>
      ),
    },
    {
      field: "delete_reason",
      headerName: "取引停止理由",
      minWidth: 280,
      flex: 1.2,
      renderCell: (params) => {
        const reason = params.row.delete_reason;

        if (!reason) {
          return (
            <Chip
              size="small"
              label="理由未登録"
              sx={{
                color: muiTheme.palette.error.main,
                backgroundColor: alpha(muiTheme.palette.error.main, 0.12),
                border: `1px solid ${alpha(muiTheme.palette.error.main, 0.32)}`,
                fontWeight: 700,
              }}
            />
          );
        }

        return (
          <Tooltip title={reason}>
            <Typography
              sx={{
                color: muiTheme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {reason}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "操作",
      width: 190,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={(event) => {
              event.stopPropagation();
              fetchDetail(params.row);
            }}
          >
            詳細
          </Button>


        </Stack>
      ),
    },
  ];

  const handleRowDoubleClick = (params: GridRowParams<StoppedCustomerRow>) => {
    fetchDetail(params.row);
  };

  const gridSx = {
    border: "none",
    color: muiTheme.palette.text.primary,
    backgroundColor: muiTheme.palette.background.paper,

    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#1F1D30",
      borderBottom: `1px solid ${muiTheme.palette.divider}`,
    },

    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#1F1D30",
    },

    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 800,
      color: muiTheme.palette.text.primary,
    },

    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${muiTheme.palette.divider}`,
      color: muiTheme.palette.text.primary,
    },

    "& .MuiDataGrid-row:hover": {
      backgroundColor: alpha("#ff8579", 0.06),
    },

    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${muiTheme.palette.divider}`,
      backgroundColor: "#1F1D30",
      color: "rgba(232,238,245,0.75)",
    },

    "& .MuiTablePagination-root": {
      color: "rgba(232,238,245,0.75)",
    },

    "& .MuiSvgIcon-root": {
      color: "rgba(232,238,245,0.75)",
    },

    "& .MuiDataGrid-overlay": {
      backgroundColor: muiTheme.palette.background.paper,
      color: "rgba(232,238,245,0.75)",
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          html: {
            overflowY: "hidden",
          },
          body: {
            overflowY: "hidden",
          },
          "#root": {
            overflowY: "hidden",
          },
        }}
      />

      <Box
        sx={{
          // ページ全体ではスクロールさせず、一覧表の中だけスクロールさせる
          height: {
            xs: "calc(100dvh - 96px)",
            md: "calc(100dvh - 150px)",
          },
          maxHeight: {
            xs: "calc(100dvh - 96px)",
            md: "calc(100dvh - 150px)",
          },
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: "background.default",
          p: { xs: 1.25, md: 2 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "flex-end" }}
              spacing={2}
            >
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{ color: "text.primary" }}
                >
                  取引停止企業
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(232,238,245,0.72)",
                    mt: 0.5,
                  }}
                >
                  取引停止した企業の情報、停止理由、最終取引日時、停止までの履歴を確認します。
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                <TextField
                  label="会社名・理由・担当者で検索"
                  size="small"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "rgba(232,238,245,0.65)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: "100%", sm: 320 },
                  }}
                />

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={fetchData}
                  disabled={loading}
                >
                  再取得
                </Button>
              </Stack>
            </Stack>
          </Box>

          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage(null)}
              sx={{ borderRadius: 2 }}
            >
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert
              severity="success"
              onClose={() => setSuccessMessage(null)}
              sx={{ borderRadius: 2 }}
            >
              {successMessage}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              border: `1px solid ${muiTheme.palette.divider}`,
              backgroundColor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${muiTheme.palette.divider}`,
                backgroundColor: "secondary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <BusinessOutlinedIcon sx={{ color: "#ff8579" }} />

                <Typography fontWeight={800} sx={{ color: "text.primary" }}>
                  取引停止企業一覧
                </Typography>

                <Chip
                  size="small"
                  label={`${filteredRows.length}件`}
                  sx={{
                    color: "#ff8579",
                    backgroundColor: alpha("#ff8579", 0.12),
                    border: `1px solid ${alpha(
                      muiTheme.palette.primary.main,
                      0.32
                    )}`,
                    fontWeight: 700,
                  }}
                />
              </Stack>
            </Box>

            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(232,238,245,0.75)",
                }}
              >
                <CircularProgress size={28} />
                <Typography sx={{ ml: 1.5 }}>読み込み中...</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <DataGridPro<StoppedCustomerRow>
                  rows={filteredRows}
                  columns={columns}
                  sx={{
                    ...gridSx,
                    height: "100%",
                  }}
                  localeText={jaJPText}
                  loading={loading}
                  rowHeight={76}
                  autoPageSize
                  disableRowSelectionOnClick
                  onRowDoubleClick={handleRowDoubleClick}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: "deleted_day", sort: "desc" }],
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        </Stack>

        <Dialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>取引停止企業詳細</DialogTitle>

          <DialogContent dividers>
            {selectedRow && (
              <Stack spacing={2}>
                {detailErrorMessage && (
                  <Alert severity="warning">{detailErrorMessage}</Alert>
                )}

                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    backgroundColor: "secondary.main",
                  }}
                >
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <InfoBlock
                          icon={<BusinessOutlinedIcon />}
                          label="会社名"
                          value={selectedRow.company_name || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <InfoBlock
                          icon={<CalendarMonthOutlinedIcon />}
                          label="最終取引日時"
                          value={formatDateTime(getLastTradeDate(selectedRow))}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <InfoBlock
                          icon={<PersonOutlineOutlinedIcon />}
                          label="最終担当"
                          value={selectedRow.last_sales_person || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <InfoBlock
                          label="取引停止日"
                          value={formatDateTime(selectedRow.deleted_day)}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <InfoBlock
                          label="使用停止者"
                          value={selectedRow.deleted_by || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 4 }}>
                        <InfoBlock
                          label="停止前状況"
                          value={selectedRow.latest_sales_status || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <InfoBlock
                          label="取引停止理由"
                          value={selectedRow.delete_reason || "理由未登録"}
                          multiline
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    backgroundColor: "secondary.main",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography fontWeight={800}>
                          取引停止までの履歴
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: "rgba(232,238,245,0.72)" }}
                        >
                          履歴1、履歴2... の順で表示します。
                        </Typography>
                      </Box>

                      <Chip
                        icon={<HistoryOutlinedIcon />}
                        label={`${salesHistories.length}件`}
                        sx={{
                          width: "fit-content",
                          color: "#ff8579",
                          backgroundColor: alpha(
                            muiTheme.palette.primary.main,
                            0.12
                          ),
                          border: `1px solid ${alpha(
                            muiTheme.palette.primary.main,
                            0.32
                          )}`,
                          fontWeight: 700,
                        }}
                      />
                    </Stack>

                    {historyLoading ? (
                      <Box
                        sx={{
                          py: 5,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CircularProgress size={28} />
                        <Typography
                          sx={{ ml: 1.5, color: "rgba(232,238,245,0.72)" }}
                        >
                          履歴を読み込み中...
                        </Typography>
                      </Box>
                    ) : salesHistories.length === 0 ? (
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          border: `1px dashed ${alpha(
                            muiTheme.palette.primary.main,
                            0.35
                          )}`,
                          backgroundColor: "#1F2530",
                          textAlign: "center",
                        }}
                      >
                        <Typography sx={{ color: "rgba(232,238,245,0.72)" }}>
                          取引履歴がありません。
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1.5}>
                        {salesHistories.map((history, index) => (
                          <HistoryCard
                            key={`${history.sales_history_id}-${index}`}
                            history={history}
                            index={index}
                            onOpenDetail={setSelectedHistory}
                          />
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    backgroundColor: "secondary.main",
                  }}
                >
                  <CardContent>
                    <Typography fontWeight={800} sx={{ mb: 2 }}>
                      会社基本情報
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoBlock
                          icon={<PhoneOutlinedIcon />}
                          label="電話番号"
                          value={selectedRow.company_telephone || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoBlock
                          icon={<EmailOutlinedIcon />}
                          label="メール"
                          value={selectedRow.company_mail || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoBlock
                          icon={<LanguageOutlinedIcon />}
                          label="サイトURL"
                          value={selectedRow.company_website || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoBlock
                          icon={<BusinessOutlinedIcon />}
                          label="業種"
                          value={selectedRow.business_description || "-"}
                        />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <InfoBlock
                          icon={<LocationOnOutlinedIcon />}
                          label="住所"
                          value={selectedRow.company_address || "-"}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            {selectedRow && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<RestartAltOutlinedIcon />}
                onClick={() => setReactivateTarget(selectedRow)}
              >
                取引再開
              </Button>
            )}

            <Button
              variant="outlined"
              color="primary"
              onClick={() => setDetailOpen(false)}
            >
              閉じる
            </Button>
          </DialogActions>
        </Dialog>

        <HistoryDetailDialog
          open={Boolean(selectedHistory)}
          history={selectedHistory}
          onClose={() => setSelectedHistory(null)}
        />

        <Dialog
          open={Boolean(reactivateTarget)}
          onClose={() => {
            if (!reactivating) setReactivateTarget(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>取引再開の確認</DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography>以下の企業を取引再開しますか？</Typography>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${muiTheme.palette.divider}`,
                  backgroundColor: "#1F2530",
                }}
              >
                <Typography fontWeight={800}>
                  {reactivateTarget?.company_name || "-"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "rgba(232,238,245,0.72)" }}
                >
                  企業ID：{reactivateTarget?.company_id}
                </Typography>
              </Box>

              <Alert severity="info">
                取引再開すると、通常の顧客一覧に再表示されます。
              </Alert>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setReactivateTarget(null)}
              disabled={reactivating}
            >
              キャンセル
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<RestartAltOutlinedIcon />}
              onClick={handleReactivate}
              disabled={reactivating}
            >
              {reactivating ? "処理中..." : "取引再開する"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

/* =========================================================
 小コンポーネント
========================================================= */

type InfoBlockProps = {
  icon?: React.ReactNode;
  label: string;
  value: string;
  multiline?: boolean;
};

const InfoBlock = ({
  icon,
  label,
  value,
  multiline = false,
}: InfoBlockProps) => {
  const muiTheme = useTheme();

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "#1F2530",
        border: `1px solid ${muiTheme.palette.divider}`,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon && (
          <Box
            sx={{
              color: "#ff8579",
              display: "flex",
            }}
          >
            {icon}
          </Box>
        )}

        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: "rgba(232,238,245,0.65)" }}
        >
          {label}
        </Typography>
      </Stack>

      <Typography
        fontWeight={700}
        sx={{
          color: "text.primary",
          mt: 0.75,
          lineHeight: 1.7,
          whiteSpace: multiline ? "pre-wrap" : "normal",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

type HistoryCardProps = {
  history: SalesHistoryRow;
  index: number;
  onOpenDetail: (history: SalesHistoryRow) => void;
};

const HistoryCard = ({ history, index, onOpenDetail }: HistoryCardProps) => {
  const muiTheme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${muiTheme.palette.divider}`,
        backgroundColor: "#1F2530",
      }}
    >
      <CardContent sx={{ p: 1.75 }}>
        <Stack spacing={1.4}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                label={`履歴 ${index + 1}`}
                size="small"
                sx={{
                  color: "#ff8579",
                  backgroundColor: alpha("#ff8579", 0.12),
                  border: `1px solid ${alpha("#ff8579", 0.32)}`,
                  fontWeight: 800,
                }}
              />

              <Typography fontWeight={800} sx={{ color: "text.primary" }}>
                {formatDateTime(history.sales_date)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={history.sales_status || "状況未設定"}
                size="small"
                sx={{
                  color: "rgba(232,238,245,0.85)",
                  backgroundColor: alpha(muiTheme.palette.secondary.main, 0.45),
                  border: `1px solid ${muiTheme.palette.divider}`,
                  fontWeight: 700,
                }}
              />

              <Chip
                icon={<PersonOutlineOutlinedIcon />}
                label={history.sales_person || "-"}
                size="small"
                sx={{
                  color: "rgba(232,238,245,0.85)",
                  backgroundColor: alpha(muiTheme.palette.secondary.main, 0.45),
                  border: `1px solid ${muiTheme.palette.divider}`,
                  fontWeight: 700,
                  "& .MuiChip-icon": {
                    color: "rgba(232,238,245,0.72)",
                  },
                }}
              />

              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityOutlinedIcon />}
                onClick={() => onOpenDetail(history)}
                sx={{
                  height: 28,
                  color: "#ff8579",
                  borderColor: alpha("#ff8579", 0.45),
                  fontWeight: 800,
                  "&:hover": {
                    borderColor: "#ff8579",
                    backgroundColor: alpha("#ff8579", 0.1),
                  },
                }}
              >
                詳細
              </Button>
            </Stack>
          </Stack>

          <Divider />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SmallInfo
                label="購入意思"
                value={history.purchase_intention || "-"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <SmallInfo
                label="提案サービス"
                value={history.proposed_service || "-"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <SmallInfo
                label="次回アクション"
                value={history.next_action || "-"}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <SmallInfo
                label="概要"
                value={history.memo || history.proposal_details || "-"}
                multiline
              />
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
};

type HistoryDetailDialogProps = {
  open: boolean;
  history: SalesHistoryRow | null;
  onClose: () => void;
};

const HistoryDetailDialog = ({
  open,
  history,
  onClose,
}: HistoryDetailDialogProps) => {
  const muiTheme = useTheme();

  if (!history) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#2D2C3A",
          color: "text.primary",
          borderRadius: 3,
          border: `1px solid ${muiTheme.palette.divider}`,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background:
            "linear-gradient(135deg, rgba(255,133,121,0.16), rgba(31,37,48,0.98))",
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography fontWeight={900}>営業履歴詳細</Typography>
            <Typography variant="body2" sx={{ color: "rgba(232,238,245,0.72)", mt: 0.5 }}>
              {formatDateTime(history.sales_date)} / {history.sales_person || "担当者未設定"}
            </Typography>
          </Box>

          <Chip
            label={history.sales_status || "状況未設定"}
            sx={{
              width: "fit-content",
              color: "#ff8579",
              backgroundColor: alpha("#ff8579", 0.12),
              border: `1px solid ${alpha("#ff8579", 0.35)}`,
              fontWeight: 800,
            }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "#2D2C3A" }}>
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock label="営業日" value={formatDateTime(history.sales_date)} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock label="営業担当者" value={history.sales_person || "-"} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock label="顧客窓口" value={history.contact_person || "-"} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock label="営業方法" value={history.sales_approach_method || "-"} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock label="購入意思" value={history.purchase_intention || "-"} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <InfoBlock
                label="対応時間"
                value={
                  history.man_hours === null || Number.isNaN(history.man_hours)
                    ? "-"
                    : `${history.man_hours}分`
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <InfoBlock label="提案サービス" value={history.proposed_service || "-"} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <InfoBlock label="次回アクション" value={history.next_action || "-"} />
            </Grid>
          </Grid>

          <InfoBlock
            label="提案内容"
            value={history.proposal_details || "-"}
            multiline
          />

          <InfoBlock
            label="振り返りメモ"
            value={history.memo || "-"}
            multiline
          />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <InfoBlock label="登録者" value={history.created_by || "-"} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <InfoBlock label="登録日" value={formatDateTime(history.created_day)} />
            </Grid>

            {(history.edited_by || history.edited_day) && (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <InfoBlock label="最終編集者" value={history.edited_by || "-"} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <InfoBlock label="最終編集日" value={formatDateTime(history.edited_day)} />
                </Grid>
              </>
            )}
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: "#1F1D30",
          borderTop: `1px solid ${muiTheme.palette.divider}`,
        }}
      >
        <Button variant="outlined" color="primary" onClick={onClose}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type SmallInfoProps = {
  label: string;
  value: string;
  multiline?: boolean;
};

const SmallInfo = ({ label, value, multiline = false }: SmallInfoProps) => {
  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color: "rgba(232,238,245,0.65)" }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: "text.primary",
          fontWeight: 600,
          mt: 0.25,
          lineHeight: 1.7,
          whiteSpace: multiline ? "pre-wrap" : "normal",
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default StoppedCustomersData;